import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Building2, MapPin, UsersRound, Users as UsersIcon, ClipboardList,
  CheckCircle2, MapPinOff, Plus, AlertTriangle, Network, FileText, Info,
} from "lucide-react";

function Tile({ icon: Icon, label, value, testid, tint = "bg-primary/10 text-primary" }) {
  return (
    <Card className="p-4" data-testid={testid}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-xs text-muted-foreground uppercase tracking-wider">{label}</div>
          <div className="text-2xl font-semibold mt-1">{value ?? "—"}</div>
        </div>
        <div className={`w-9 h-9 rounded-full flex items-center justify-center ${tint}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </Card>
  );
}

export default function DistributionDashboard() {
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setError(null);
    try {
      const { data } = await api.get("/distribution/dashboard");
      setStats(data);
    } catch (e) {
      const msg = formatApiError(e);
      setError(msg);
      toast.error(msg);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (error && !stats) {
    return (
      <Card className="p-6 flex items-start gap-3 bg-red-50 border-red-200" data-testid="dm-dashboard-error">
        <AlertTriangle className="w-5 h-5 text-red-700 shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="font-medium text-red-900">Could not load your dashboard</div>
          <div className="text-sm text-red-800 mt-1">{error}</div>
          <Button variant="outline" size="sm" className="mt-3" onClick={load} data-testid="dm-dashboard-retry">
            Retry
          </Button>
        </div>
      </Card>
    );
  }

  if (!stats) {
    return (
      <div className="text-sm text-muted-foreground">Loading dashboard…</div>
    );
  }

  // -------------------------------------------------------------------
  // Distributor's personalised dashboard — only their own city + parties
  // -------------------------------------------------------------------
  if (stats.view === "distributor") {
    const isRegistered = stats.registration_status === "Registered";
    return (
      <div className="space-y-6" data-testid="dm-dashboard-distributor">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-display font-bold text-2xl tracking-tight">
              Welcome, {stats.distributor_name || "Distributor"}
            </h1>
            <p className="text-sm text-muted-foreground">
              {stats.city ? `${stats.city}${stats.state ? ", " + stats.state : ""}` : "City not set"}
              {stats.distributor_code && (
                <> · <span className="font-mono">{stats.distributor_code}</span></>
              )}
            </p>
          </div>
          <Button
            onClick={() => navigate("/distribution/parties")}
            data-testid="dm-dist-add-party-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> Add / Manage Parties
          </Button>
        </div>

        {!isRegistered && (
          <Card className="p-4 bg-amber-50 border-amber-200 flex items-start gap-3" data-testid="dm-dist-pending-banner">
            <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
            <div className="text-sm">
              <div className="font-medium text-amber-900">Your registration is pending</div>
              <div className="text-amber-800 mt-0.5">
                The accountant will upload your Proforma / Tax invoice to complete your registration and assign your distributor code.
              </div>
            </div>
          </Card>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Tile icon={UsersRound} label="My Parties" value={stats.my_parties_total} testid="dm-my-parties-total" tint="bg-purple-100 text-purple-700" />
          <Tile icon={CheckCircle2} label="Registered" value={stats.my_parties_registered} testid="dm-my-parties-registered" tint="bg-green-100 text-green-700" />
          <Tile icon={ClipboardList} label="Pending" value={stats.my_parties_pending} testid="dm-my-parties-pending" tint="bg-orange-100 text-orange-700" />
        </div>

        <Card className="p-4 space-y-2" data-testid="dm-dist-profile-card">
          <h2 className="font-display font-semibold text-lg">Profile</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div><span className="text-muted-foreground">Distributor Code:</span> <span className="font-mono">{stats.distributor_code || "—"}</span></div>
            <div><span className="text-muted-foreground">GST:</span> {stats.gst_number || "—"}</div>
            <div><span className="text-muted-foreground">Mobile:</span> {stats.mobile || "—"}</div>
            <div><span className="text-muted-foreground">Registration:</span> <span className={`font-medium ${isRegistered ? "text-green-700" : "text-amber-700"}`}>{stats.registration_status}</span></div>
          </div>
        </Card>
      </div>
    );
  }

  // -------------------------------------------------------------------
  // IBO's personalised dashboard — own stats + team roll-up (MLM downline).
  // -------------------------------------------------------------------
  if (stats.view === "ibo") {
    return (
      <div className="space-y-6" data-testid="dm-dashboard-ibo">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-display font-bold text-2xl tracking-tight">
              Welcome, {stats.name || "IBO"}
            </h1>
            <p className="text-sm text-muted-foreground">
              {[stats.city, stats.state].filter(Boolean).join(", ") || "Location not set"} · {stats.email}
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate("/ibo/my-team")} data-testid="dm-ibo-team-btn">
              <Network className="w-4 h-4 mr-1" /> My Team
            </Button>
            <Button onClick={() => navigate("/distribution/parties")} data-testid="dm-ibo-parties-btn">
              <Plus className="w-4 h-4 mr-1" /> Add Party
            </Button>
          </div>
        </div>

        {/* Personal stats */}
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Your activity</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Tile icon={UsersRound} label="My Parties" value={stats.my_parties_total} testid="dm-ibo-my-parties" tint="bg-purple-100 text-purple-700" />
            <Tile icon={CheckCircle2} label="Registered Parties" value={stats.my_parties_registered} testid="dm-ibo-my-parties-reg" tint="bg-green-100 text-green-700" />
            <Tile icon={FileText} label="Bills Submitted" value={stats.my_bills_total} testid="dm-ibo-my-bills" tint="bg-blue-100 text-blue-700" />
            <Tile icon={ClipboardList} label="Bills Approved" value={stats.my_bills_approved} testid="dm-ibo-my-bills-approved" tint="bg-emerald-100 text-emerald-700" />
          </div>
        </div>

        {/* My territories — scoped to states/cities where the IBO or downline has business */}
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">My territories</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <button
              type="button"
              onClick={() => navigate("/distribution/territories")}
              className="text-left focus:outline-none focus:ring-2 focus:ring-primary rounded-lg"
              data-testid="dm-ibo-my-states-btn"
            >
              <Tile icon={MapPin} label="States I Operate In" value={stats.my_territories_states} testid="dm-ibo-my-states" tint="bg-sky-100 text-sky-700" />
            </button>
            <button
              type="button"
              onClick={() => navigate("/distribution/territories")}
              className="text-left focus:outline-none focus:ring-2 focus:ring-primary rounded-lg"
              data-testid="dm-ibo-my-cities-btn"
            >
              <Tile icon={Building2} label="Cities I Operate In" value={stats.my_territories_cities} testid="dm-ibo-my-cities" tint="bg-cyan-100 text-cyan-700" />
            </button>
          </div>
        </div>

        {/* Team roll-up */}
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
            My team (self + downline · {stats.team_size} member{stats.team_size === 1 ? "" : "s"})
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Tile icon={Network} label="Team Size" value={stats.team_size} testid="dm-ibo-team-size" tint="bg-indigo-100 text-indigo-700" />
            <Tile icon={UsersRound} label="Team Parties" value={stats.team_parties_total} testid="dm-ibo-team-parties" tint="bg-fuchsia-100 text-fuchsia-700" />
            <Tile icon={CheckCircle2} label="Team Registered" value={stats.team_parties_registered} testid="dm-ibo-team-parties-reg" tint="bg-green-100 text-green-700" />
            <Tile icon={FileText} label="Team Bills" value={stats.team_bills_total} testid="dm-ibo-team-bills" tint="bg-amber-100 text-amber-700" />
          </div>
        </div>

        {/* Distributor submissions (only shown if the IBO submitted any) */}
        {(stats.my_distributors_pending + stats.my_distributors_approved) > 0 && (
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
              Distributors you registered
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Tile icon={ClipboardList} label="Awaiting Admin Approval" value={stats.my_distributors_pending} testid="dm-ibo-dist-pending" tint="bg-amber-100 text-amber-700" />
              <Tile icon={CheckCircle2} label="Approved" value={stats.my_distributors_approved} testid="dm-ibo-dist-approved" tint="bg-green-100 text-green-700" />
            </div>
          </div>
        )}
      </div>
    );
  }

  // -------------------------------------------------------------------
  // Restricted view — for support roles (accountant / backend_executive /
  // market_developer / sales) who used to see the company snapshot.
  // -------------------------------------------------------------------
  if (stats.view === "restricted") {
    return (
      <div className="space-y-4" data-testid="dm-dashboard-restricted">
        <div>
          <h1 className="font-display font-bold text-2xl tracking-tight">Distribution</h1>
          <p className="text-sm text-muted-foreground">Overview.</p>
        </div>
        <Card className="p-6 flex items-start gap-3 bg-blue-50 border-blue-200">
          <Info className="w-5 h-5 text-blue-700 shrink-0 mt-0.5" />
          <div className="text-sm">
            <div className="font-medium text-blue-900">Company snapshot is admin-only</div>
            <div className="text-blue-800 mt-1">
              {stats.message || "Use the Parties and Distributors pages in the sidebar to work with your scoped data."}
            </div>
          </div>
        </Card>
      </div>
    );
  }

  // -------------------------------------------------------------------
  // Company-wide dashboard (admin only)
  // -------------------------------------------------------------------
  return (
    <div className="space-y-6" data-testid="dm-dashboard-company">
      <div>
        <h1 className="font-display font-bold text-2xl tracking-tight">Distribution Dashboard</h1>
        <p className="text-sm text-muted-foreground">Snapshot of territories, distributors and parties.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <Tile icon={MapPin} label="Total States" value={stats.total_states} testid="dm-stat-states" tint="bg-blue-100 text-blue-700" />
        <Tile icon={MapPin} label="Total Cities" value={stats.total_cities} testid="dm-stat-cities" tint="bg-indigo-100 text-indigo-700" />
        <Tile icon={Building2} label="Total Distributors" value={stats.total_distributors} testid="dm-stat-distributors" tint="bg-emerald-100 text-emerald-700" />
        <Tile icon={UsersRound} label="Total Parties" value={stats.total_parties} testid="dm-stat-parties" tint="bg-purple-100 text-purple-700" />
        <Tile icon={UsersIcon} label="Total IBOs" value={stats.total_ibos} testid="dm-stat-ibos" tint="bg-amber-100 text-amber-700" />
        <Tile icon={ClipboardList} label="Pending Registrations" value={stats.pending_registrations} testid="dm-stat-pending" tint="bg-orange-100 text-orange-700" />
        <Tile icon={AlertTriangle} label="Distributors Awaiting Approval" value={stats.distributors_pending_admin_approval} testid="dm-stat-dist-pending-approval" tint="bg-red-100 text-red-700" />
        <Tile icon={CheckCircle2} label="Registered Parties" value={stats.registered_parties} testid="dm-stat-registered" tint="bg-green-100 text-green-700" />
        <Tile icon={MapPinOff} label="Cities without Distributor" value={stats.cities_without_distributor} testid="dm-stat-noDist" tint="bg-red-100 text-red-700" />
        <Tile icon={UsersIcon} label="Company + IBO Cities" value={stats.company_ibo_managed_cities} testid="dm-stat-ibo-cities" tint="bg-slate-200 text-slate-700" />
      </div>

      {stats.cities_without_distributor_list?.length > 0 && (
        <Card className="p-4 space-y-2" data-testid="dm-cities-no-dist">
          <h2 className="font-display font-semibold text-lg">Cities without distributor</h2>
          <ul className="text-sm divide-y">
            {stats.cities_without_distributor_list.map((c, i) => (
              <li key={i} className="py-1.5 flex justify-between">
                <span>{c.city}</span>
                <span className="text-xs text-muted-foreground">{c.state}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}
